import React from 'react';
import { Check, ShieldCheck, Stethoscope, Activity, BarChart3 } from 'lucide-react';
import { useReveal } from '../hooks/useInView';
import { BRAND_NAME } from '../utils/brand';

const REASONS = [
  {
    icon: ShieldCheck,
    title: 'Safety-gated by design',
    points: [
      'Auto-rejects absolute contraindications',
      'High-risk profiles routed to a doctor',
      'No injection without MMC + APC clearance'
    ]
  },
  {
    icon: Stethoscope,
    title: 'Doctor-prescribed only',
    points: [
      'MMC-registered, APC-current doctors',
      'Authorised to prescribe and administer',
      'Same clinic for every follow-up'
    ]
  },
  {
    icon: Activity,
    title: 'Globally approved meds',
    points: [
      'FDA, EMA, HSA, NPRA-approved',
      'Dual-action GIP + GLP-1 mechanism',
      'Used worldwide for weight & metabolic care'
    ]
  },
  {
    icon: BarChart3,
    title: 'Outcome-driven matching',
    points: [
      'Clinic ratings based on real results',
      'Weekly weight + photo tracking',
      'Better matches over time as data grows'
    ]
  }
];

function ReasonCard({ r, i }) {
  const rev = useReveal({ threshold: 0.1 });
  const Icon = r.icon;
  return (
    <div ref={rev.ref} className={`${rev.className} card-soft p-7 h-full`} style={{ transitionDelay: `${i * 60}ms` }}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
          <Icon size={20} />
        </div>
        <h3 className="text-lg font-semibold text-ink-900">{r.title}</h3>
      </div>
      <ul className="mt-5 space-y-2.5">
        {r.points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-ink-700">
            <Check size={16} className="text-brand-700 mt-0.5 shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function WhyChooseUs() {
  const head = useReveal();
  return (
    <section className="section bg-white">
      <div className="container-x">
        <div ref={head.ref} className={`${head.className} max-w-3xl`}>
          <span className="eyebrow">Why {BRAND_NAME}</span>
          <h2 className="h-section mt-4">Built around safety and outcomes — not hype.</h2>
          <p className="lead mt-4">
            We're not a pharmacy or a clinic. We're the layer between you and a verified
            clinic network — gated by an eligibility quiz and proven by outcome data.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {REASONS.map((r, i) => <ReasonCard key={r.title} r={r} i={i} />)}
        </div>
      </div>
    </section>
  );
}
